// src/utils/insuranceDossierPdf.js
// Insurance Dossier (§5 assureur / §17 Phase 2) — cédule d'assurance d'une collection.
// Inventaire + valeur à assurer + état/localisation (risque) + police existante (table insurance).
// ARTKIV fournit la donnée ; l'assureur analyse valeur et risque. Ne vaut pas police (§13).
// Langues : fr (défaut), en, de — paramètre `lang`.
// jsPDF v3 : import jsPDF from "jspdf" + autoTable(doc, {...}).
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { BRAND_UPPER } from "../lib/rebrand";
import { valuationFooter, valuationDate, isStaleValuation } from "./valuationDisclosure";

const LANGS = ["fr", "en", "de"];
const pickLang = (lang) => {
  const l = String(lang || "fr").slice(0, 2).toLowerCase();
  return LANGS.includes(l) ? l : "fr";
};
const LOCALE = { fr: "fr-FR", en: "en-GB", de: "de-DE" };

const T = {
  fr: {
    condition: { excellent: "Excellent", very_good: "Très bon", good: "Bon", fair: "Moyen", very_poor: "Très moyen", poor: "Mauvais", heavily_damaged: "Très abîmé", partially_restorable: "Part. restaurable", irreparable: "Irréparable" },
    title: "DOSSIER D'ASSURANCE — CÉDULE",
    collectionFallback: "Collection", holder: "Titulaire",
    summary: ["Nombre d'œuvres", "Valeur totale à assurer", "Œuvres assurées", "Valeur déjà assurée"],
    scheduleTitle: "Cédule des œuvres",
    head: ["Œuvre", "Artiste", "Valeur à assurer", "Fourchette", "Estimée le", "État", "Localisation", "Assureur", "N° police", "Échéance"],
    untitled: "(sans titre)", notInsured: "— (non assurée)", old: " (ancienne)",
    foot: (brand, date) =>
      `Cédule établie via ${brand} le ${date}. Document informatif destiné à l'analyse assurantielle : il ne constitue pas une police d'assurance. ${brand} structure la donnée (valeur, documentation, risque) ; l'assureur apprécie la valeur et le risque.`,
    fileSlug: "dossier-assurance",
  },
  en: {
    condition: { excellent: "Excellent", very_good: "Very good", good: "Good", fair: "Fair", very_poor: "Very poor", poor: "Poor", heavily_damaged: "Heavily damaged", partially_restorable: "Partly restorable", irreparable: "Irreparable" },
    title: "INSURANCE FILE — SCHEDULE",
    collectionFallback: "Collection", holder: "Holder",
    summary: ["Number of works", "Total value to insure", "Works insured", "Value already insured"],
    scheduleTitle: "Schedule of works",
    head: ["Work", "Artist", "Value to insure", "Range", "Estimated on", "Condition", "Location", "Insurer", "Policy no.", "Expiry"],
    untitled: "(untitled)", notInsured: "— (not insured)", old: " (old)",
    foot: (brand, date) =>
      `Schedule produced with ${brand} on ${date}. Informative document for insurance analysis: it is not an insurance policy. ${brand} organises the data (value, documentation, risk); the insurer assesses the value and the risk.`,
    fileSlug: "insurance-file",
  },
  de: {
    condition: { excellent: "Ausgezeichnet", very_good: "Sehr gut", good: "Gut", fair: "Mittel", very_poor: "Sehr schlecht", poor: "Schlecht", heavily_damaged: "Stark beschädigt", partially_restorable: "Teilweise restaurierbar", irreparable: "Nicht reparierbar" },
    title: "VERSICHERUNGSAKTE — VERZEICHNIS",
    collectionFallback: "Sammlung", holder: "Inhaber",
    summary: ["Anzahl der Werke", "Gesamtwert zur Versicherung", "Versicherte Werke", "Bereits versicherter Wert"],
    scheduleTitle: "Verzeichnis der Werke",
    head: ["Werk", "Künstler", "Zu versichernder Wert", "Spanne", "Geschätzt am", "Zustand", "Standort", "Versicherer", "Policen-Nr.", "Ablauf"],
    untitled: "(ohne Titel)", notInsured: "— (nicht versichert)", old: " (veraltet)",
    foot: (brand, date) =>
      `Verzeichnis, erstellt mit ${brand} am ${date}. Informationsdokument für die versicherungstechnische Prüfung: Es ist keine Versicherungspolice. ${brand} strukturiert die Daten (Wert, Dokumentation, Risiko); der Versicherer beurteilt Wert und Risiko.`,
    fileSlug: "versicherungsakte",
  },
};

const currentValue = (a) =>
  Number(a?.current_estimated_value ?? a?.valuation_estimated_value ?? a?.estimated_value ?? a?.value ?? 0) || 0;

// insByArtwork : { [artwork_id]: { insurer, insurance_type, value, expiry_date, idnumber_insurance } }
export function generateInsuranceDossier({ collectionName = "", ownerName = "", artworks = [], insByArtwork = {}, lang = "fr" } = {}) {
  const lg = pickLang(lang);
  const L = T[lg];
  const loc = LOCALE[lg];
  const eur = (v) => (v || v === 0 ? `${Number(v).toLocaleString(loc)} €` : "—");
  const fmt = (d) => (d ? new Date(d).toLocaleDateString(loc) : "—");
  const rangeOf = (a) => {
    const lo = Number(a?.valuation_low), hi = Number(a?.valuation_high);
    return lo || hi ? `${lo ? eur(lo) : "—"} – ${hi ? eur(hi) : "—"}` : "—";
  };
  const estimatedOn = (a) => {
    const d = valuationDate(a);
    return d ? fmt(d) + (isStaleValuation(d) ? L.old : "") : "—";
  };

  const doc = new jsPDF("l", "mm", "a4"); // paysage : cédule large
  const M = 14;
  let y = 16;

  const total = artworks.length;
  const totalToInsure = artworks.reduce((s, a) => s + currentValue(a), 0);
  const insured = artworks.filter((a) => insByArtwork[a.id]);
  const insuredValue = insured.reduce((s, a) => s + (Number(insByArtwork[a.id]?.value) || currentValue(a)), 0);

  // ---- En-tête ----
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(150);
  doc.text(L.title, M, y);
  doc.setFontSize(17); doc.setTextColor(20);
  y += 8; doc.text(doc.splitTextToSize(collectionName || L.collectionFallback, 260), M, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90);
  y += 6; if (ownerName) { doc.text(`${L.holder} : ${ownerName}`, M, y); y += 4; }

  // ---- Synthèse ----
  autoTable(doc, {
    startY: y + 2, theme: "plain", margin: { left: M, right: M },
    styles: { fontSize: 10, cellPadding: 1.5 },
    columnStyles: { 0: { fontStyle: "bold", textColor: 120, cellWidth: 70 } },
    body: [
      [L.summary[0], String(total)],
      [L.summary[1], eur(totalToInsure)],
      [L.summary[2], `${insured.length} / ${total}`],
      [L.summary[3], eur(insuredValue)],
    ],
  });
  y = (doc.lastAutoTable?.finalY || y) + 7;

  // ---- Cédule ----
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(25);
  doc.text(L.scheduleTitle, M, y);
  autoTable(doc, {
    startY: y + 3, margin: { left: M, right: M },
    head: [L.head],
    body: artworks
      .slice()
      .sort((a, b) => currentValue(b) - currentValue(a))
      .map((a) => {
        const ins = insByArtwork[a.id];
        return [
          a.title || L.untitled,
          a.artist || "—",
          eur(currentValue(a)),
          rangeOf(a),
          estimatedOn(a),
          L.condition[a.condition] || a.condition || "—",
          a.storage_location_detail || "—",
          ins?.insurer || L.notInsured,
          ins?.idnumber_insurance || "—",
          ins ? fmt(ins.expiry_date) : "—",
        ];
      }),
    headStyles: { fillColor: [26, 26, 26], fontSize: 8 },
    styles: { fontSize: 8, cellPadding: 1.4 },
    columnStyles: { 2: { halign: "right" } },
    // Ligne surlignée si non assurée
    didParseCell: (data) => {
      if (data.section === "body" && data.row.raw[7] === L.notInsured) {
        data.cell.styles.textColor = [180, 68, 58];
      }
    },
  });
  y = (doc.lastAutoTable?.finalY || y) + 5;

  // ---- Pied assureur (§5/§13) ----
  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(140);
  const foot = valuationFooter(artworks, lg) + " " + L.foot(BRAND_UPPER, new Date().toLocaleDateString(loc));
  doc.text(doc.splitTextToSize(foot, 265), M, 195);

  const slug = (collectionName || "collection").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  doc.save(`${L.fileSlug}-${slug || "collection"}.pdf`);
}
